import { useRef, useState } from "react";
import { BookOpen, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errText } from "@/components/control/kit";

const DOCS = [  // [key, title, download name, key read inline instead (ZIP packages)]
  ["api-vertrag", "Gemeinsamer API-Vertrag – Paket Stand 2.8 (Basis 2.7.1 + Nachträge 2.7.2, 2.8)", "visibilis-api-vertrag-v2.8-paket.zip", "api-vertrag-liesmich"],
  ["cockpit-core-vertrag", "Organisations-Cockpit ↔ Core – Zuordnung", "visibilis-cockpit-core-zuordnung.md"],
  ["feed-nachtrag", "Mitglieder-App: Feed-Nachtrag zu v2.6", "visibilis-mitglieder-feed-nachtrag-v2.6.md"],
  ["einladung-einloesen", "Mitglieder-Einladung einlösen – Vertrag für die Einlöseseite", "visibilis-mitglieder-einladung-einloesen.md"],
  ["cockpit-cors-vorschlag", "Cockpit-CORS-Vorschlag (nicht aktiviert)", "visibilis-cockpit-cors-vorschlag.md"],
  ["cockpit-cors-diff", "Cockpit-CORS-Diff (nicht angewendet)", "visibilis-cockpit-cors-vorschlag.diff"],
  ["restliste", "Restliste C40 · C97 · C113 · C87", "visibilis-restliste-c40-c97-c113-c87.md"],
  ["testnachweise", "DEV-Testnachweise Wegwerf-Stack (Berichte, Protokolle, Bilder)", "visibilis-dev-testnachweise-2026-10-25.zip", "testnachweise-liesmich"],
];

async function fetchDoc(key) {
  const res = await api.get(`/settings/handoff/${key}`, { responseType: "blob" });
  const buf = await res.data.arrayBuffer();
  const digest = window.crypto?.subtle ? await window.crypto.subtle.digest("SHA-256", buf) : null;
  const sha = digest ? Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("") : "";
  return { blob: res.data, text: new TextDecoder().decode(buf), sha };
}

function Reader({ doc, onClose, returnFocus }) {
  return (
    <Dialog open={!!doc} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[88vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto" data-testid="handoff-reader"
        onCloseAutoFocus={(e) => { e.preventDefault(); returnFocus.current?.focus(); }}>
        <DialogHeader>
          <DialogTitle>{doc?.title}</DialogTitle>
          <DialogDescription className="[overflow-wrap:anywhere]">{doc?.sha ? `SHA-256 ${doc.sha}` : "Nur lesen – nichts wird geändert."}</DialogDescription>
        </DialogHeader>
        <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-zinc-800 [overflow-wrap:anywhere]" data-testid="handoff-reader-text">{doc?.text}</pre>
      </DialogContent>
    </Dialog>
  );
}

export function HandoffDocs() {
  const [busy, setBusy] = useState("");
  const [reading, setReading] = useState(null);
  const opener = useRef(null);
  const run = async (key, title, filename) => {
    opener.current = document.activeElement;
    setBusy(key + (filename ? ":download" : ":read"));
    try {
      const doc = await fetchDoc(key);
      if (!filename) {
        setReading({ title, ...doc });
        return;
      }
      const url = URL.createObjectURL(doc.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`${title} heruntergeladen${doc.sha ? ` · SHA-256 ${doc.sha.slice(0, 12)}…` : ""}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="mb-4 rounded-xl border border-[#E3DDF0] bg-[#F7F5FB] p-3" aria-labelledby="handoff-title" data-testid="handoff-docs">
      <h3 id="handoff-title" className="text-sm font-semibold text-brand-purple">Übergabe-Unterlagen</h3>
      <p className="mt-1 text-sm text-zinc-700">Ohne Geheimnisse und ohne personenbezogene Daten – für Mitglieder-App und Organisations-Cockpit. Nur nach Anmeldung abrufbar.</p>
      <ul className="mt-3 divide-y divide-[#E3DDF0]">
        {DOCS.map(([key, title, filename, readKey]) => (
          <li key={key} className="flex flex-wrap items-center gap-2 py-2" data-testid={`handoff-${key}`}>
            <span className="min-w-0 basis-full text-sm font-medium text-zinc-800 sm:basis-0 sm:flex-1">{title}</span>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run(readKey || key, readKey ? `${title} – Lese- und Geltungsreihenfolge` : title)}
              aria-label={readKey ? `${title}: Lese- und Geltungsreihenfolge lesen` : `${title} lesen`} data-testid={`handoff-${key}-read`}>
              <BookOpen className="mr-1 h-4 w-4" aria-hidden="true" />{readKey ? "Reihenfolge lesen" : "Lesen"}
            </Button>
            <Button size="sm" disabled={!!busy} onClick={() => run(key, title, filename)} aria-label={`${title} herunterladen`}
              data-testid={`handoff-${key}-download`} className="bg-brand-magenta hover:bg-brand-magenta-dark">
              <Download className="mr-1 h-4 w-4" aria-hidden="true" />Herunterladen
            </Button>
          </li>
        ))}
      </ul>
      <Reader doc={reading} onClose={() => setReading(null)} returnFocus={opener} />
    </section>
  );
}
